import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User } from '@/types';
import { usersApi } from '@/api/endpoints/users';
import toast from 'react-hot-toast';

interface UserState {
  users: User[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchUsers: () => Promise<void>;
  createUser: (user: Partial<User>) => Promise<void>;
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  inviteUser: (email: string, role: string) => Promise<void>;
}

export const useUserStore = create<UserState>()(
  devtools(
    (set, get) => ({
      users: [],
      loading: false,
      error: null,

      fetchUsers: async (params?: { search?: string; role?: string; status?: string }) => {
        set({ loading: true, error: null });
        try {
          const response = await usersApi.getUsers(params);
          // console.log('response: ', response);          
          set({ users: response?.data?.data?.users, loading: false });
        } catch (error: any) {
          set({ error: error.response?.error.message || error.message , loading: false });
          toast.error('Failed to fetch users');
        }
      },

      createUser: async (user) => {
        try {
          const response = await usersApi.createUser(user);
          set((state) => ({
            users: [...state.users, response.data],
          }));
          toast.success('User created successfully');
        } catch (error) {
          toast.error('Failed to create user');
        }
      },

      updateUser: async (id, updates) => {
        try {
          const response = await usersApi.updateUser(id, updates);
          set((state) => ({
            users: state.users.map(user => 
              user.id === id ? response.data : user
            ),
          }));
          toast.success('User updated successfully');
        } catch (error) {
          toast.error('Failed to update user');
        }
      },

      deleteUser: async (id) => {
        try {
          await usersApi.deleteUser(id);
          set((state) => ({
            users: state.users.filter(user => user.id !== id),
          }));
          toast.success('User deleted successfully');
        } catch (error) {
          toast.error('Failed to delete user');
        }
      },

      inviteUser: async (email, role) => {
        try {
          const response = await usersApi.inviteUser({ email, role });
          set((state) => ({
            users: [...state.users, response.data],
          }));
          toast.success('User invitation sent successfully');
        } catch (error) {
          toast.error('Failed to invite user');
        }
      },
    }),
    {
      name: 'user-store',
    }
  )
);